/**
 * Copyright Highway9 Networks Inc.
 */
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "..";
import insightService from "~/services/insight-service";
import type { Insight } from "~/types/Insight";

export const insightsAdapter = createEntityAdapter<Insight>({
  selectId: (insight) =>
    insight.id ||
    `${insight.insightId}:${insight.source?.id ?? ""}:${insight.startTimestamp ?? ""}`,
});

interface InsightState {
  insightsData: EntityState<Insight>;
  loading: boolean;
  sourceFilter: string;
}

export const initialState: InsightState = {
  insightsData: insightsAdapter.getInitialState(),
  loading: true,
  sourceFilter: "edge",
};

export const fetchInsights = createAsyncThunk(
  "itDashboard/fetchInsights",
  async (filter: Partial<Insight>) => {
    const response = await insightService.getInsights(filter);
    return response;
  },
);

const insightSlice = createSlice({
  name: "insight",
  initialState,
  reducers: {
    setInsights: (state, action: PayloadAction<Insight[]>) => {
      insightsAdapter.setAll(state.insightsData, action.payload);
      state.loading = false;
    },
    setSourceFilter: (state, action: PayloadAction<string>) => {
      state.sourceFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInsights.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchInsights.fulfilled, (state, action) => {
      insightsAdapter.setAll(state.insightsData, action.payload);
      state.loading = false;
    });
    builder.addCase(fetchInsights.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const insightAction = insightSlice.actions;

const insightEntitySelectors = insightsAdapter.getSelectors(
  (state: RootState) => state.insight.insightsData,
);

export const insightData = insightEntitySelectors.selectAll;
export const insightEntities = insightEntitySelectors.selectEntities;

export const insightsLoading = (state: RootState) => state.insight.loading;
export const insightSourceFilter = (state: RootState) => state.insight.sourceFilter;

export default insightSlice.reducer;
